import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { fetchOrderByBillNo, updateOrderStatus } from "../../../api/api";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { usePrivileges } from "../../../core/context/PrivilegeContext";

interface OrderDetailsType {
  BillDiscAmtField: string | number | readonly string[] | undefined;
  BillDiscPercField: string | number | readonly string[] | undefined;
  BillTaxField: string | number | readonly string[] | undefined;
  Lorry: string | number | readonly string[] | undefined;
  TotDiscPercAmt: number;
  BillNo: number;
  BillPartyName: string;
  BillDate: string;
  BillNetTotalField: number;
  godown: string;
}

const OrderDetails = () => {

  //const { privileges } = usePrivileges();
  //const canUpdateOrderStatus = privileges.includes("order_status_update");

  const { t } = useTranslation();
  const { billNo } = useParams<{ billNo: string }>();
  const [order, setOrder] = useState<{ master: OrderDetailsType; sales: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState("pending");
  const [orderNote, setOrderNote] = useState("");

  //Recorder states
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);

  //for admin privileges
  const token = localStorage.getItem("token");

let isAdmin = false;

if (token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    isAdmin = payload?.isAdmin === true;
  } catch (err) {
    console.error("Invalid token", err);
  }
}

const { privileges } = usePrivileges();

const canUpdateOrderStatus =
  isAdmin || privileges.includes("order_status_update");

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const response = await fetchOrderByBillNo(billNo || "");
        console.log("Fetched Order Detail:", response);
        setOrder(response);
        console.log("Order Status:", response.status?.[0]?.order_status);
        setOrderStatus(response.status?.[0]?.order_status || "pending"); //fetching status and notes from backend
        setOrderNote(response.status?.[0]?.order_note || "");
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setLoading(false);
      }
    };

    if (billNo) {
      loadOrder();
    }
  }, [billNo]);

  //Start Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        chunks.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        chunks.current = [];
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setVoiceBlob(blob);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (error) {
      console.error("Mic access error:", error);
    }
  };

  //Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  // update orderstatus
  const handleUpdateOrder = async () => {

     if (!canUpdateOrderStatus) {
    alert("No permission to update order status");
    return;
  }


    if (!order?.master) return;

    const todaydate = dayjs().format("YYYY-MM-DD HH:mm:ss");
    //const user = localStorage.getItem("username") || "unknown@example.com"; //user info from localstorage 

    const payload = {
      billno: order.master.BillNo,
      order_status: orderStatus,
      order_note: orderNote,
      date1: todaydate,
      partyname: order.master.BillPartyName,
      net_total: order.master.BillNetTotalField,
      //user: user,
    };

    try {
      await updateOrderStatus(payload, voiceBlob); // pass blob here
      alert("Order status updated successfully!");
    } catch (err: any) {
      if (err.response && err.response.status === 409) {
        const backendMessage = err.response.data?.message || "";
        if (backendMessage === "Order with same billno and order_status already exists") {
          alert("This order status has already been saved.");
        } else {
          alert("Conflict: " + backendMessage);
        }
      } else {
        alert("Something went wrong while updating order status.");
        console.error(err);
      }
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!order || !order.master) return <p>Order not found.</p>;
  const { master } = order;

  return (
    <div className="page-wrapper">
      <div
        className="content container-fluid"
        style={{ maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}
      >
        <h4 className="mb-4">
          {t("orderDetails")} - {master.BillNo}
        </h4>

       
        {/* Status, Notes & Recorder */} 
        <div className="row mb-4">
          <div className="col-md-4">
            <label className="form-label form-label-sm">{t("status")}</label>
            <select
              className="form-select form-select-sm"
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
            >
              <option value="pending">{t("pending")}</option>
              <option value="Customer Responded">{t("customer Responded")}</option>
              <option value="Approved">{t("approved")}</option>
              <option value="On Work">{t("on Work")}</option>
              <option value="Completed">{t("completed")}</option>
              <option value="Invoice Issued">{t("invoice Issued")}</option>
            </select>
          </div>

          
          <div className="col-md-5 d-flex align-items-end">
            <div style={{ width: "100%" }}>
              <label className="form-label form-label-sm">{t("notes")}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={t("enterNotes")}
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                style={{ height: "30px", fontSize: "0.8rem" }}
              />
            </div>
          </div>

          
          <div className="col-md-3 d-flex align-items-end">
              <div>
                <label className="form-label form-label-sm">{t("voiceNote")}</label>
                <div>
                  {recording ? (
                    <button
                      className="btn btn-danger me-2"
                      onClick={stopRecording}
                      style={{ padding: "8px 15px", fontSize: "0.80rem" }} 
                    >
                      {t("stop")}
                    </button>
                  ) : (
                    <button
                      className="btn btn-success me-2"
                      onClick={startRecording}
                      style={{ padding: "8px 18px", fontSize: "0.80rem" }} 
                    >
                      {t("record")}
                    </button>
                  )}
                </div>
                {audioUrl && (
                  <audio
                    controls
                    src={audioUrl}
                    style={{ display: "block", marginTop: "4px", maxWidth: "180px" }}
                  />
                )}
              </div>
            </div>
          </div>
        
          {/* Order info */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <strong>{t("billNo")}</strong> {master.BillNo}
          </div>
          <div className="col-md-6 mb-3">
            <strong>{t("partyName")}:</strong> {master.BillPartyName}
          </div>
          <div className="col-md-6 mb-3">
            <strong>{t("date")}:</strong> {master.BillDate.slice(0, 10)}
          </div>
          <div className="col-md-6 mb-3">
            <strong>{t("total")}:</strong> ₹{master.BillNetTotalField}
          </div>
          <div className="col-md-6 mb-3">
            <strong>{t("godown")}:</strong> {master.godown}
          </div>
        </div>

       {/* Sales Items */}
        {order.sales?.length > 0 && (
          <div className="mt-4">
            <h5>{t("salesItems")}</h5>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("itemCode")}</th>
                  <th>{t("itemName")}</th>
                  <th>{t("qty")}</th>
                  <th>{t("rate")}</th>
                  <th>{t("total")}</th>
                </tr>
              </thead>
              <tbody>
                {order.sales.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.ItemC}</td>
                    <td>{item.Item}</td>
                    <td>{item.Qty}</td>
                    <td>{item.Rate}</td>
                    <td>{item.Total}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Update button */}
            <div className="d-flex justify-content-end mt-4">
             <button
  className="btn px-4"
  style={{
    backgroundColor: canUpdateOrderStatus ? "#007bff" : "#6c757d",
    color: "#fff",
    cursor: canUpdateOrderStatus ? "pointer" : "not-allowed",
    border: "none",
  }}
  onClick={handleUpdateOrder}
>
  {t("Update")}
</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
